class EndlessScroll {
  static instances = new Map();
  static autoLoadRootMargin = '0px 0px 150px 0px';
  static returnPositionStorageKey = 'neighbourhood:collection-return-position';
  static returnPositionMaxAge = 24 * 60 * 60 * 1000;
  static returnPositionHistoryKey = 'neighbourhoodCollectionReturnToken';
  static returnPositionTrackingBound = false;
  static returnPositionRestorePending = false;
  static returnPositionRestored = false;
  static revealAnimationClasses = [
    'scroll-trigger',
    'scroll-trigger--offscreen',
    'animate--slide-in',
    'animate--fade-in',
    'scroll-trigger--cancel',
  ];

  static init(root = document) {
    const endlessScrollElements = root.querySelectorAll('[data-endless-scroll]');

    endlessScrollElements.forEach((element) => {
      if (EndlessScroll.instances.has(element)) return;
      EndlessScroll.instances.set(element, new EndlessScroll(element));
    });

    EndlessScroll.instances.forEach((instance, element) => {
      if (document.body.contains(element)) return;
      instance.destroy();
      EndlessScroll.instances.delete(element);
    });

    EndlessScroll.bindReturnPositionTracking();
    EndlessScroll.restoreReturnPosition();
  }

  static bindReturnPositionTracking() {
    if (EndlessScroll.returnPositionTrackingBound) return;

    document.addEventListener('click', EndlessScroll.saveReturnPosition);
    window.addEventListener('pageshow', (event) => {
      EndlessScroll.restoreReturnPosition(event.persisted);
    });
    EndlessScroll.returnPositionTrackingBound = true;
  }

  static getProductPath(href) {
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin || !/(^|\/)products\/[^/]+/.test(url.pathname)) return '';
      return url.pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  static getHistoryToken() {
    const currentState = history.state && typeof history.state === 'object' ? history.state : {};
    let token = currentState[EndlessScroll.returnPositionHistoryKey];
    if (token) return token;

    token = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

    try {
      history.replaceState(
        { ...currentState, [EndlessScroll.returnPositionHistoryKey]: token },
        '',
        window.location.href
      );
    } catch (error) {
      return '';
    }

    return token;
  }

  static saveReturnPosition(event) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link = event.target.closest('[data-endless-scroll-grid] > .grid__item a[href]');
    if (!link || link.target === '_blank') return;

    const productPath = EndlessScroll.getProductPath(link.href);
    const item = link.closest('.grid__item');
    const historyToken = EndlessScroll.getHistoryToken();
    if (!productPath || !item || !historyToken) return;

    const record = {
      collectionUrl: `${window.location.pathname}${window.location.search}`,
      historyToken,
      productPath,
      viewportOffset: item.getBoundingClientRect().top,
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(EndlessScroll.returnPositionStorageKey, JSON.stringify(record));
      EndlessScroll.returnPositionRestored = false;
    } catch (error) {
      return;
    }
  }

  static readReturnPosition() {
    try {
      const record = JSON.parse(sessionStorage.getItem(EndlessScroll.returnPositionStorageKey));
      if (!record || Date.now() - record.savedAt > EndlessScroll.returnPositionMaxAge) return null;
      return record;
    } catch (error) {
      return null;
    }
  }

  static findProductItem(grid, productPath) {
    const items = grid.querySelectorAll(':scope > .grid__item');

    return Array.from(items).find((item) =>
      Array.from(item.querySelectorAll('a[href]')).some(
        (link) => EndlessScroll.getProductPath(link.href) === productPath
      )
    );
  }

  static alignProductItem(item, viewportOffset) {
    const targetTop = window.scrollY + item.getBoundingClientRect().top - viewportOffset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
  }

  static async restoreReturnPosition(fromBackForwardCache = false) {
    if (EndlessScroll.returnPositionRestorePending || EndlessScroll.returnPositionRestored) return;

    const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
    if (!fromBackForwardCache && navigationEntry?.type !== 'back_forward') return;

    const record = EndlessScroll.readReturnPosition();
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const currentHistoryToken = history.state?.[EndlessScroll.returnPositionHistoryKey];
    if (
      !record ||
      record.collectionUrl !== currentUrl ||
      !currentHistoryToken ||
      record.historyToken !== currentHistoryToken
    ) {
      return;
    }

    const grid = document.querySelector('[data-endless-scroll-grid]');
    if (!grid) return;

    EndlessScroll.returnPositionRestorePending = true;

    try {
      let item = EndlessScroll.findProductItem(grid, record.productPath);
      const instance = Array.from(EndlessScroll.instances.values()).find(
        (candidate) => candidate.grid === grid
      );
      let loadedPages = 0;

      while (!item && instance?.nextUrl && loadedPages < 50) {
        const pageLoaded = await instance.loadNextPage();
        if (!pageLoaded) break;
        loadedPages += 1;
        item = EndlessScroll.findProductItem(grid, record.productPath);
      }

      if (!item) return;

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      EndlessScroll.alignProductItem(item, record.viewportOffset);
      window.setTimeout(() => EndlessScroll.alignProductItem(item, record.viewportOffset), 100);
      EndlessScroll.returnPositionRestored = true;
    } finally {
      EndlessScroll.returnPositionRestorePending = false;
    }
  }

  constructor(element) {
    this.element = element;
    this.area = this.element.closest('[data-endless-scroll-area]');
    this.gridContainer = this.element.closest('#ProductGridContainer');
    this.grid = this.gridContainer?.querySelector('[data-endless-scroll-grid]');
    this.status = this.element.querySelector('[data-endless-scroll-status]');
    this.loadingText = this.element.dataset.loadingText || 'Loading...';
    this.sectionId = this.element.dataset.sectionId;
    this.nextUrl = this.element.dataset.nextUrl;
    this.isLoading = false;
    this.loadingPromise = null;
    this.observer = null;
    this.onIntersection = this.onIntersection.bind(this);

    if (!this.area || !this.gridContainer || !this.grid || !this.nextUrl) {
      this.element.hidden = true;
      return;
    }

    this.paginationWrapper = this.area.querySelector('.pagination-wrapper');
    this.area.classList.remove('is-fallback-visible');
    this.area.classList.add('endless-scroll-enabled');
    this.observe();
  }

  observe() {
    if (this.observer) this.observer.disconnect();

    this.observer = new IntersectionObserver(this.onIntersection, {
      rootMargin: EndlessScroll.autoLoadRootMargin,
    });

    this.observer.observe(this.element);
  }

  onIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || this.isLoading || !this.nextUrl) return;
      this.loadNextPage();
    });
  }

  stripRevealAnimation(element) {
    const animatedElements = [
      element,
      ...element.querySelectorAll(
        '.scroll-trigger, .scroll-trigger--offscreen, .scroll-trigger--cancel, .animate--slide-in, .animate--fade-in, [data-cascade], [style*="--animation-order"]'
      ),
    ];

    animatedElements.forEach((animatedElement) => {
      animatedElement.classList.remove(...EndlessScroll.revealAnimationClasses);
      animatedElement.removeAttribute('data-cascade');
      animatedElement.style.removeProperty('--animation-order');
      if (!animatedElement.getAttribute('style')) animatedElement.removeAttribute('style');
    });
  }

  async loadNextPage() {
    if (this.isLoading) return this.loadingPromise;
    if (!this.nextUrl) return false;

    this.loadingPromise = this.fetchNextPage();
    return this.loadingPromise;
  }

  async fetchNextPage() {
    if (!this.nextUrl) return false;

    this.isLoading = true;
    this.element.setAttribute('aria-busy', 'true');
    this.element.setAttribute('aria-label', this.loadingText);
    if (this.status) this.status.textContent = this.loadingText;

    try {
      const requestUrl = new URL(this.nextUrl, window.location.origin);
      if (this.sectionId) requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString());
      if (!response.ok) throw new Error(`Failed to fetch page ${requestUrl.toString()}`);

      const responseText = await response.text();
      const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const nextGrid = responseHTML.querySelector('[data-endless-scroll-grid]');
      const nextItems = nextGrid ? Array.from(nextGrid.children).filter((item) => item.matches('.grid__item')) : [];

      if (nextItems.length === 0) {
        this.area?.classList.add('is-fallback-visible');
        this.stopAutoLoading(true);
        return false;
      }

      const fragment = document.createDocumentFragment();
      nextItems.forEach((item) => {
        const nextItem = document.importNode(item, true);
        this.stripRevealAnimation(nextItem);
        fragment.appendChild(nextItem);
      });
      this.grid.appendChild(fragment);

      const nextArea = responseHTML.querySelector('[data-endless-scroll-area]');
      const nextPaginationWrapper = nextArea?.querySelector('.pagination-wrapper');
      if (nextPaginationWrapper && this.paginationWrapper) {
        this.paginationWrapper.replaceWith(document.importNode(nextPaginationWrapper, true));
        this.paginationWrapper = this.area.querySelector('.pagination-wrapper');
      }

      const nextControl = responseHTML.querySelector('[data-endless-scroll]');
      this.nextUrl = nextControl?.dataset.nextUrl || '';
      this.element.dataset.nextUrl = this.nextUrl;

      if (!this.nextUrl) {
        this.stopAutoLoading(true);
        return true;
      }
      return true;
    } catch (error) {
      console.error('Endless scroll failed', error);
      this.area?.classList.add('is-fallback-visible');
      this.stopAutoLoading(true);
      return false;
    } finally {
      this.isLoading = false;
      this.loadingPromise = null;
      this.element.removeAttribute('aria-busy');
      this.element.removeAttribute('aria-label');
      if (this.status) this.status.textContent = '';
      if (this.nextUrl && this.observer) this.observe();
    }
  }

  stopAutoLoading(hideControl) {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    this.nextUrl = '';
    this.element.dataset.nextUrl = '';
    if (hideControl) this.element.hidden = true;
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  EndlessScroll.init();
});

document.addEventListener('product-grid:updated', (event) => {
  EndlessScroll.init(event.detail?.container || document);
});
